/**
 * Epic Analytics Summary Component
 * 
 * Summary metrics panel displaying high-level statistics
 * for the selected time period and project.
 */

import React from 'react'
import { EpicAnalyticsSummaryProps } from '../../../../types/epic-analytics-types'

/**
 * Epic Analytics Summary - High-level metrics display
 */
export const EpicAnalyticsSummary: React.FC<EpicAnalyticsSummaryProps> = ({
  totalEpics,
  totalPoints,
  totalBugs,
  unanalyzedEpics,
  onUnanalyzedClick,
  loading = false
}) => {
  if (loading) {
    return (
      <div className="epic-analytics-summary">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, index) => (
            <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="epic-analytics-summary">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total Epics */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm font-medium text-gray-500 mb-1">
            Total Epics
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {totalEpics.toLocaleString()}
          </div>
        </div>

        {/* Total Points */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm font-medium text-gray-500 mb-1">
            Story Points
          </div>
          <div className="text-2xl font-bold text-blue-600">
            {totalPoints.toLocaleString()}
          </div>
        </div>

        {/* Total Bugs */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm font-medium text-gray-500 mb-1">
            Bugs Delivered
          </div>
          <div className="text-2xl font-bold text-red-600">
            {totalBugs.toLocaleString()}
          </div>
        </div>

        {/* Unanalyzed Epics */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="text-sm font-medium text-gray-500 mb-1">
            Unanalyzed Epics
          </div>
          <div className="flex items-center">
            <div className="text-2xl font-bold text-amber-600 mr-2">
              {unanalyzedEpics.toLocaleString()}
            </div>
            {unanalyzedEpics > 0 && (
              <button
                onClick={onUnanalyzedClick}
                className="text-sm text-blue-600 hover:text-blue-800 underline"
                title="View unanalyzed epics"
              >
                View
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default EpicAnalyticsSummary